// Per-chain config for everything bridge-sdk-js needs. Mainnet and
// testnet are both supplied; the active one is picked by
// `defaultNetwork()`. Addresses match `bridge-cli/src/defaults.rs` in
// Near-One/bridge-sdk-rs so the backend's NEAR-side connector list and
// the frontend's chain-side addresses point at the same deployment.

import type { Network } from "@/config/network";

export interface EvmChainCfg {
  /** EIP-155 chain id (decimal). */
  id: number;
  /** Human label for the UI. */
  label: string;
  /** HTTP JSON-RPC endpoint used for view-call reads. */
  rpcUrl: string;
  /** OmniBridge contract address (0x-hex). */
  omniBridgeAddress: string;
  /** Block-explorer base URL for tx links. */
  explorer: string;
}

export interface SolanaChainCfg {
  /** Cluster slug per @solana/web3.js — `mainnet-beta` or `devnet`. */
  cluster: "mainnet-beta" | "devnet";
  /** Cluster RPC URL. */
  rpcUrl: string;
  /** Omni-bridge program id (base58). */
  programId: string;
  /** Wormhole core program id. */
  wormholeCore: string;
  /** Wormhole post-message-shim program id. */
  shimProgramId: string;
  /** Wormhole post-message-shim event authority. */
  shimEventAuthority: string;
  /** Explorer base. */
  explorer: string;
}

/** A non-Solana SVM chain (e.g. Fogo). fin_transfer uses the SDK's own
 *  program addresses (`createFogoBuilder`), so the app only needs the RPC
 *  endpoint (for the `Connection`) and an explorer base for tx links. */
export interface SvmChainCfg {
  /** SVM cluster RPC URL. */
  rpcUrl: string;
  /** Explorer base for tx links. */
  explorer: string;
}

export interface StarknetChainCfg {
  /** 0x-hex felt — `0x534e5f5345504f4c4941` (SN_SEPOLIA) or `0x534e5f4d41494e` (SN_MAIN). */
  chainId: `0x${string}`;
  rpcUrl: string;
  /** OmniBridge contract address (0x-hex felt). */
  omniBridgeAddress: string;
  /** Explorer base. */
  explorer: string;
}

export interface BtcChainCfg {
  /** Public Bitcoin RPC the indexer-backed lookup falls back to. */
  rpcUrl: string;
  /** Explorer base (mempool.space or similar). */
  explorer: string;
}

export interface ChainBundle {
  network: Network;
  evm: Record<"eth" | "arb" | "base" | "bnb" | "pol" | "abs", EvmChainCfg>;
  solana: SolanaChainCfg;
  /** Fogo — SVM chain finalized via `@omni-bridge/solana` `createFogoBuilder`. */
  fogo: SvmChainCfg;
  starknet: StarknetChainCfg;
  btc: BtcChainCfg;
  zcash: BtcChainCfg;
  /** OmniBridge indexer base URL — same on both nets (different hosts). */
  bridgeIndexerUrl: string;
}

const TESTNET: ChainBundle = {
  network: "testnet",
  evm: {
    eth: {
      id: 11_155_111,
      label: "Sepolia",
      rpcUrl: "https://ethereum-sepolia-rpc.publicnode.com",
      omniBridgeAddress: "0x68a86e0Ea5B1d39F385c1326e4d493526dFe4401",
      explorer: "https://sepolia.etherscan.io",
    },
    arb: {
      id: 421_614,
      label: "Arbitrum Sepolia",
      rpcUrl: "https://arbitrum-sepolia-rpc.publicnode.com",
      omniBridgeAddress: "0x0C981337fFe39a555d3A40dbb32f21aD0eF33FFA",
      explorer: "https://sepolia.arbiscan.io",
    },
    base: {
      id: 84_532,
      label: "Base Sepolia",
      rpcUrl: "https://base-sepolia-rpc.publicnode.com",
      omniBridgeAddress: "0xa56b860017152cD296ad723E8409Abd6e5D86d4d",
      explorer: "https://sepolia.basescan.org",
    },
    bnb: {
      id: 97,
      label: "BNB Smart Chain (testnet)",
      rpcUrl: "https://bsc-testnet-rpc.publicnode.com",
      omniBridgeAddress: "0xEC81aFc3485a425347Ac03316675e58a680b283A",
      explorer: "https://testnet.bscscan.com",
    },
    pol: {
      id: 80_002,
      label: "Polygon Amoy",
      rpcUrl: "https://polygon-amoy-bor-rpc.publicnode.com",
      omniBridgeAddress: "0xEC81aFc3485a425347Ac03316675e58a680b283A",
      explorer: "https://amoy.polygonscan.com",
    },
    abs: {
      id: 11_124,
      label: "Abstract testnet",
      rpcUrl: "https://api.testnet.abs.xyz",
      omniBridgeAddress: "0x5C79627d2cD753d45B41839d187619f99c7B8D78",
      explorer: "https://explorer.testnet.abs.xyz",
    },
  },
  solana: {
    cluster: "devnet",
    rpcUrl: "https://api.devnet.solana.com",
    programId: "862HdJV59Vp83PbcubUnvuXc4EAXP8CDDs6LTxFpunTe",
    wormholeCore: "3u8hJUVTA4jH1wYAyUur7FFZVQ8H635K3tSHHF4ssjQ5",
    shimProgramId: "EtZMZM22ViKMo4r5y4Anovs3wKQ2owUmDpjygnMMcdEX",
    shimEventAuthority: "HQS31aApX3DDkuXgSpV9XyDUNtFgQ31pUn5BNWHG2PSp",
    explorer: "https://explorer.solana.com",
  },
  fogo: {
    // RPC matches @omni-bridge/solana DEFAULT_FOGO_RPC_URLS. The bridge
    // program addresses come from the SDK (createFogoBuilder); if Fogo isn't
    // deployed on this network the SDK surfaces a clear "not yet deployed".
    rpcUrl: "https://testnet.fogo.io",
    explorer: "https://fogoscan.com",
  },
  starknet: {
    chainId: "0x534e5f5345504f4c4941",
    rpcUrl: "https://starknet-sepolia-rpc.publicnode.com",
    omniBridgeAddress:
      "0x02830785fd87b181c5391819f4a5e6a0b2d76c49d92b7f748a2433495eead162",
    explorer: "https://sepolia.voyager.online",
  },
  btc: {
    rpcUrl: "https://bitcoin-testnet-rpc.publicnode.com",
    explorer: "https://mempool.space/testnet",
  },
  zcash: {
    rpcUrl: "https://zcash-testnet.gateway.tatum.io/",
    explorer: "https://blockexplorer.one/zcash/testnet",
  },
  bridgeIndexerUrl: "https://testnet.api.bridge.nearone.org",
};

const MAINNET: ChainBundle = {
  network: "mainnet",
  evm: {
    eth: {
      id: 1,
      label: "Ethereum",
      rpcUrl: "https://ethereum-rpc.publicnode.com",
      // From @omni-bridge/core MAINNET_ADDRESSES (== bridge-cli defaults.rs).
      omniBridgeAddress: "0xe00c629afaccb0510995a2b95560e446a24c85b9",
      explorer: "https://etherscan.io",
    },
    arb: {
      id: 42_161,
      label: "Arbitrum",
      rpcUrl: "https://arbitrum-one-rpc.publicnode.com",
      omniBridgeAddress: "0xd025b38762B4A4E36F0Cde483b86CB13ea00D989",
      explorer: "https://arbiscan.io",
    },
    base: {
      id: 8453,
      label: "Base",
      rpcUrl: "https://base-rpc.publicnode.com",
      omniBridgeAddress: "0xd025b38762B4A4E36F0Cde483b86CB13ea00D989",
      explorer: "https://basescan.org",
    },
    bnb: {
      id: 56,
      label: "BNB Smart Chain",
      rpcUrl: "https://bsc-rpc.publicnode.com",
      omniBridgeAddress: "0x073C8a225c8Cf9d3f9157F5C1a1DbE02407f5720",
      explorer: "https://bscscan.com",
    },
    pol: {
      id: 137,
      label: "Polygon",
      rpcUrl: "https://polygon-bor-rpc.publicnode.com",
      omniBridgeAddress: "0xd025b38762B4A4E36F0Cde483b86CB13ea00D989",
      explorer: "https://polygonscan.com",
    },
    abs: {
      id: 2741,
      label: "Abstract",
      rpcUrl: "https://api.mainnet.abs.xyz",
      omniBridgeAddress: "0xd2490A00bDB97C1EDE4fdf207CFE2664AFB9C20D",
      explorer: "https://explorer.mainnet.abs.xyz",
    },
  },
  solana: {
    cluster: "mainnet-beta",
    rpcUrl: "https://api.mainnet-beta.solana.com",
    programId: "dahPEoZGXfyV58JqqH85okdHmpN8U2q8owgPUXSCPxe",
    wormholeCore: "worm2ZoG2kUd4vFXhvjh93UUH596ayRfgQ2MgjNMTth",
    shimProgramId: "EtZMZM22ViKMo4r5y4Anovs3wKQ2owUmDpjygnMMcdEX",
    shimEventAuthority: "HQS31aApX3DDkuXgSpV9XyDUNtFgQ31pUn5BNWHG2PSp",
    explorer: "https://explorer.solana.com",
  },
  fogo: {
    rpcUrl: "https://mainnet.fogo.io",
    explorer: "https://fogoscan.com",
  },
  starknet: {
    chainId: "0x534e5f4d41494e",
    rpcUrl: "https://starknet-rpc.publicnode.com",
    omniBridgeAddress:
      "0x05f9a4a841dfb7bb3cde33073b2450fe45dcd407fb6c0985a274b0e943ad8598",
    explorer: "https://voyager.online",
  },
  btc: {
    rpcUrl: "https://bitcoin-rpc.publicnode.com",
    explorer: "https://mempool.space",
  },
  zcash: {
    rpcUrl: "https://zcash-mainnet.gateway.tatum.io/",
    explorer: "https://blockexplorer.one/zcash/mainnet",
  },
  bridgeIndexerUrl: "https://mainnet.api.bridge.nearone.org",
};

export function chains(network: Network): ChainBundle {
  return network === "mainnet" ? MAINNET : TESTNET;
}
